/**
 * User API Service
 * Handles tai-khoan container API requests for:
 * - User authentication and profile management
 * - Role determination
 * - Stripe subscription integration
 */
import { getApiUrl, isDevelopment } from '../../config/environment';
import { createApiResponse, createApiError } from '../../utils/apiHelpers';

class UserApiService {
  constructor() {
    this.serviceName = 'UserAPI';
    this.baseURL = getApiUrl('user');
    this.isOnline = false;
    this.lastChecked = null;
    
    console.log(`[${this.serviceName}] Service initialized with base URL: ${this.baseURL}`);
  }
  
  /**
   * Get or create user data using Auth0 user info
   * @param {Object} user - Auth0 user object
   * @param {string} accessToken - Auth0 access token
   * @returns {Promise<Object>} - Standardized API response with user data
   */
  async getOrCreateUser(user, accessToken) {
    try {
      console.log(`[${this.serviceName}] Getting user data for:`, {
        user_id: user.sub,
        user_name: user.name || user.nickname,
        user_email: user.email
      });
      
      // Construct URL properly for both absolute and relative paths
      const fullUrl = this.baseURL.startsWith('http') 
        ? `${this.baseURL}/getuser`
        : `${window.location.origin}${this.baseURL}/getuser`;
      
      const url = new URL(fullUrl);
      
      // Add query parameters
      url.searchParams.append('user_id', user.sub);
      url.searchParams.append('user_name', user.name || user.nickname || '');
      url.searchParams.append('user_email', user.email || '');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      
      // Update service status
      this.isOnline = true;
      this.lastChecked = new Date();
      
      console.log(`[${this.serviceName}] User data retrieved successfully:`, {
        id: userData.id,
        email: userData.user_email,
        role: userData.user_role,
        plan_status: userData.plan_status
      });
      
      return createApiResponse(userData, 'success', null, {
        source: 'tai-khoan-api'
      });
      
    } catch (error) {
      this.isOnline = false;
      this.lastChecked = new Date();
      
      console.error(`[${this.serviceName}] Get user data failed:`, error);
      return Promise.reject(createApiError(error, this.serviceName));
    }
  }
  
  /**
   * Test connectivity to user API
   * @returns {Promise<Object>} - Connectivity test results
   */
  async testConnectivity() {
    const startTime = Date.now();
    
    try {
      console.log(`[${this.serviceName}] Testing connectivity to: ${this.baseURL}`);
      
      // Use a simple OPTIONS request to test CORS and connectivity without crashing the service
      const fullUrl = this.baseURL.startsWith('http') 
        ? `${this.baseURL}/getuser`
        : `${window.location.origin}${this.baseURL}/getuser`;

      const response = await fetch(fullUrl, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'GET',
          'Access-Control-Request-Headers': 'Content-Type,Authorization',
        },
        mode: 'cors',
      });
      
      const duration = Date.now() - startTime;
      const isOnline = response.ok || response.status < 500;
      
      this.isOnline = isOnline;
      this.lastChecked = new Date();
      
      console.log(`[${this.serviceName}] Connectivity test ${isOnline ? 'PASSED' : 'FAILED'} in ${duration}ms - Status: ${response.status}`);
      
      return {
        isOnline,
        status: response.status,
        duration,
        rateLimitInfo: null // tai-khoan doesn't use rate limiting
      };
      
    } catch (error) {
      const duration = Date.now() - startTime;
      console.warn(`[${this.serviceName}] Connectivity test FAILED after ${duration}ms:`, error.message);
      
      this.isOnline = false;
      this.lastChecked = new Date();
      
      return {
        isOnline: false,
        status: null,
        duration,
        rateLimitInfo: null,
        error: error.message
      };
    }
  }
  
  /**
   * Determine user role based on database role and Stripe subscription
   * @param {Object} userResponse - User data from API
   * @param {string} roleOverride - Optional role override
   * @returns {string} - Final determined user role
   */
  determineUserRole(userResponse, roleOverride = '') {
    // Stripe plan mappings
    const STRIPE_PLANS = {
      STANDARD_PROD: 'price_1R2Oi7Dvj5PcWFuPWhmI4Tzk',
      STANDARD_TEST: 'price_standard_test_id', 
      PREMIUM_PROD: 'price_premium_prod_id',
      PREMIUM_TEST: 'price_premium_test_id'
    };
    
    // Start with override or database role
    let role = roleOverride || userResponse.user_role || 'Basic';
    const planStatus = userResponse.plan_status;
    const planId = userResponse.plan_id;

    console.log(`[${this.serviceName}] Determining user role:`, {
      databaseRole: userResponse.user_role,
      planStatus,
      planId,
      roleOverride,
      currentRole: role
    });

    // Override user role with Stripe plan if active subscription AND not Admin/Dev
    if (planStatus === 'active' && !['Admin', 'Dev'].includes(role)) {
      if (planId === STRIPE_PLANS.STANDARD_PROD || planId === STRIPE_PLANS.STANDARD_TEST) {
        role = 'Standard';
        console.log(`[${this.serviceName}] Overriding role to Standard due to active subscription`);
      } else if (planId === STRIPE_PLANS.PREMIUM_PROD || planId === STRIPE_PLANS.PREMIUM_TEST) {
        role = 'Premium';
        console.log(`[${this.serviceName}] Overriding role to Premium due to active subscription`);
      }
    } else if (['Admin', 'Dev'].includes(role)) {
      console.log(`[${this.serviceName}] Protected role ${role} - not overriding with subscription`);
    }

    console.log(`[${this.serviceName}] Final user role: ${role}`);
    return role;
  }
  
  /**
   * Get current service status
   */
  getStatus() {
    return {
      serviceName: this.serviceName,
      isOnline: this.isOnline,
      lastChecked: this.lastChecked,
      rateLimitInfo: null,
      baseURL: this.baseURL
    };
  }
}

// Create and export singleton instance
export default new UserApiService();